import { useEffect, useState } from "react";
import api, { apiErr, fmtMoney, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CAN_APPROVE_FUNDS, CAN_DISBURSE, FUND_CATEGORIES } from "@/lib/constants";
import { PageHeader, Loader, EmptyState } from "@/components/ui-kit";
import { StatusBadge } from "@/components/StatusBadge";
import { ValBadge } from "@/lib/validation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, HandCoins, Check, X, Banknote, Loader2, FileDown, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function FundRequests() {
  const { role } = useAuth();
  const canApprove = CAN_APPROVE_FUNDS.includes(role);
  const canDisburse = CAN_DISBURSE.includes(role);
  const [items, setItems] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({ montant: 0, motif: "", categorie: "divers" });
  const [disb, setDisb] = useState(null); // request being disbursed
  const [accId, setAccId] = useState("");
  const [refuse, setRefuse] = useState(null); // request being refused
  const [motifRefus, setMotifRefus] = useState("");

  const load = () => api.get("/fund-requests").then((r) => setItems(r.data));
  useEffect(() => { load(); if (canDisburse) api.get("/cash-accounts").then((r) => setAccounts(r.data)); }, []);

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/fund-requests", f); toast.success("Demande envoyée pour validation"); setOpen(false); setF({ montant: 0, motif: "", categorie: "divers" }); load(); }
    catch (err) { toast.error(apiErr(err)); } finally { setSaving(false); }
  };
  const decide = async (id, statut, motif_refus = "") => { try { await api.patch(`/fund-requests/${id}/decision`, { statut, motif_refus }); toast.success(`Demande ${statut.toLowerCase()}e`); load(); } catch (e) { toast.error(apiErr(e)); } };
  const doRefuse = async () => { if (!motifRefus.trim()) { toast.error("Motif du refus requis"); return; } await decide(refuse.id, "Refusé", motifRefus); setRefuse(null); setMotifRefus(""); };
  const del = async (id) => { if (!window.confirm("Supprimer définitivement cette demande de fonds ?")) return; try { await api.delete(`/fund-requests/${id}`); toast.success("Demande supprimée"); load(); } catch (e) { toast.error(apiErr(e)); } };
  const doDisburse = async () => {
    if (!accId) { toast.error("Sélectionnez une caisse"); return; }
    try { await api.patch(`/fund-requests/${disb.id}/disburse`, { account_id: accId }); toast.success("Fonds décaissés"); setDisb(null); setAccId(""); load(); if (canDisburse) api.get("/cash-accounts").then((r) => setAccounts(r.data)); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const receipt = async (r) => {
    try {
      const res = await api.get(`/fund-requests/${r.id}/receipt`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `recu_${r.numero}.pdf`; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
    } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Demandes de fonds" subtitle="Collaborateur → Validation → Décaissement par le caissier">
        <Button onClick={() => setOpen(true)} data-testid="add-fund-request" className="bg-brand-600 hover:bg-brand-700 text-white"><Plus className="h-4 w-4 mr-1.5" />Demander des fonds</Button>
      </PageHeader>

      {items === null ? <Loader /> : items.length === 0 ? (
        <EmptyState icon={HandCoins} title="Aucune demande de fonds" subtitle="Soumettez une demande ; elle sera validée puis décaissée par le caissier." />
      ) : (
        <div className="rounded-xl border bg-white overflow-hidden"><div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-slate-50"><TableHead>N°</TableHead><TableHead>Demandeur</TableHead><TableHead>Motif</TableHead><TableHead>Catégorie</TableHead><TableHead>Montant</TableHead><TableHead>Statut</TableHead><TableHead>Caisse</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((r) => (
                <TableRow key={r.id} className="hover:bg-slate-50" data-testid={`fund-row-${r.id}`}>
                  <TableCell className="font-mono text-xs text-slate-500">{r.numero}</TableCell>
                  <TableCell className="font-semibold">{r.user_name}</TableCell>
                  <TableCell className="text-sm max-w-52 truncate">{r.motif}{r.statut === "Refusé" && r.motif_refus && <span className="block text-xs text-rose-600 mt-0.5" data-testid={`refus-motif-${r.id}`}>Refus : {r.motif_refus}</span>}</TableCell>
                  <TableCell><span className="rounded bg-slate-100 px-2 py-0.5 text-xs capitalize">{r.categorie}</span></TableCell>
                  <TableCell className="font-mono text-xs font-bold">{fmtMoney(r.montant)}</TableCell>
                  <TableCell><StatusBadge value={r.statut} /><ValBadge v={r.validation} /></TableCell>
                  <TableCell className="text-sm text-slate-500">{r.account_name || "—"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1.5">
                      {canApprove && r.statut === "En attente" && (
                        <>
                          <Button size="sm" onClick={() => decide(r.id, "Approuvé")} className="bg-emerald-600 text-white h-8 px-2" data-testid={`approve-fund-${r.id}`}><Check className="h-4 w-4" /></Button>
                          <Button size="sm" onClick={() => { setRefuse(r); setMotifRefus(""); }} className="bg-rose-600 text-white h-8 px-2" data-testid={`refuse-fund-${r.id}`}><X className="h-4 w-4" /></Button>
                        </>
                      )}
                      {canDisburse && r.statut === "Approuvé" && (
                        <Button size="sm" onClick={() => { setDisb(r); setAccId(""); }} className="bg-brand-600 text-white h-8" data-testid={`disburse-${r.id}`}><Banknote className="h-4 w-4 mr-1" />Décaisser</Button>
                      )}
                      {r.statut === "Décaissé" && (
                        <Button size="sm" variant="outline" onClick={() => receipt(r)} className="h-8" data-testid={`receipt-${r.id}`}><FileDown className="h-4 w-4 mr-1" />Reçu</Button>
                      )}
                      {(canApprove || canDisburse) && (
                        <Button size="sm" variant="outline" onClick={() => del(r.id)} className="h-8 px-2 text-rose-600 border-rose-200 hover:bg-rose-50" data-testid={`delete-fund-${r.id}`}><Trash2 className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div></div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Nouvelle demande de fonds</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <div><Label className="text-xs">Montant (FCFA)</Label><Input type="number" value={f.montant} onChange={(e) => setF({ ...f, montant: Number(e.target.value) })} required className="mt-1" data-testid="fund-montant" /></div>
            <div><Label className="text-xs">Motif</Label><Textarea value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} required rows={3} className="mt-1" data-testid="fund-motif" /></div>
            <div><Label className="text-xs">Catégorie</Label>
              <Select value={f.categorie} onValueChange={(v) => setF({ ...f, categorie: v })}><SelectTrigger className="mt-1" data-testid="fund-categorie"><SelectValue /></SelectTrigger>
                <SelectContent>{FUND_CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent></Select></div>
            <DialogFooter><Button type="button" variant="outline" onClick={() => setOpen(false)}>Annuler</Button><Button type="submit" disabled={saving} className="bg-brand-600 text-white" data-testid="fund-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Envoyer"}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!refuse} onOpenChange={(o) => !o && setRefuse(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Refuser la demande</DialogTitle></DialogHeader>
          {refuse && (
            <div className="space-y-3">
              <div className="rounded-lg border bg-slate-50 p-3">
                <p className="text-sm">Demande <span className="font-mono">{refuse.numero}</span> de <span className="font-semibold">{refuse.user_name}</span></p>
                <p className="font-mono text-lg font-bold text-brand-600 mt-1">{fmtMoney(refuse.montant)}</p>
                <p className="text-xs text-slate-500 mt-1">{refuse.motif}</p>
              </div>
              <div><Label className="text-xs">Motif / commentaire du refus *</Label><Textarea value={motifRefus} onChange={(e) => setMotifRefus(e.target.value)} rows={3} className="mt-1" data-testid="refus-motif-input" placeholder="Expliquez clairement la raison du refus…" /></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setRefuse(null)}>Annuler</Button><Button onClick={doRefuse} className="bg-rose-600 text-white" data-testid="confirm-refuse"><X className="h-4 w-4 mr-1.5" />Confirmer le refus</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!disb} onOpenChange={(o) => !o && setDisb(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-head">Décaisser les fonds</DialogTitle></DialogHeader>
          {disb && (
            <div className="space-y-4">
              <div className="rounded-lg border bg-slate-50 p-4">
                <p className="text-sm">Demande <span className="font-mono">{disb.numero}</span> de <span className="font-semibold">{disb.user_name}</span></p>
                <p className="font-mono text-xl font-bold text-brand-600 mt-1">{fmtMoney(disb.montant)}</p>
                <p className="text-xs text-slate-500 mt-1">{disb.motif}</p>
              </div>
              <div><Label className="text-xs">Caisse à débiter</Label>
                <Select value={accId} onValueChange={setAccId}><SelectTrigger className="mt-1" data-testid="disburse-account"><SelectValue placeholder="Sélectionner une caisse" /></SelectTrigger>
                  <SelectContent>{accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.nom} · {fmtMoney(a.solde)}</SelectItem>)}</SelectContent></Select></div>
              <DialogFooter><Button type="button" variant="outline" onClick={() => setDisb(null)}>Annuler</Button><Button onClick={doDisburse} className="bg-emerald-600 text-white" data-testid="confirm-disburse"><Banknote className="h-4 w-4 mr-1.5" />Confirmer le décaissement</Button></DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
